import type { HTMLAttributes } from 'react';
import { type Ref } from 'react';
import './index.scss';

export interface FullBoxProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'> {
  className?: ClassNames;
  frontClassName?: ClassNames;
  centerClassName?: ClassNames;
  rearClassName?: ClassNames;
  namespace?: string;
  column?: boolean;
  fullHeight?: boolean;
  frontChildren?: ReactNode;
  children: ReactNode;
  rearChildren?: ReactNode;
  refFront?: Ref<HTMLDivElement>;
  refCenter?: Ref<HTMLDivElement>;
  refRear?: Ref<HTMLDivElement>;
  frontProps?: HTMLAttributes<HTMLDivElement>;
  centerProps?: HTMLAttributes<HTMLDivElement>;
  rearProps?: HTMLAttributes<HTMLDivElement>;
}

export default forwardRef<HTMLDivElement, FullBoxProps>(function FullBox(
  {
    className,
    frontClassName,
    centerClassName,
    rearClassName,
    namespace,
    column,
    fullHeight,
    frontChildren,
    children,
    rearChildren,
    refFront,
    refCenter,
    refRear,
    frontProps,
    centerProps,
    rearProps,
    ...props
  },
  ref
) {
  const classes = useMemo(
    () => ({
      main: cls('full_box', className, namespace, {
        full_box_column: column,
        full_box_full_height: fullHeight === undefined ? column : fullHeight,
      }),
      front: cls('full_box_front', frontClassName, {
        [namespace + '_front']: namespace,
      }),
      center: cls('full_box_center', centerClassName, {
        [namespace + '_center']: namespace,
      }),
      rear: cls('full_box_rear', rearClassName, {
        [namespace + '_rear']: namespace,
      }),
    }),
    [
      className,
      namespace,
      column,
      fullHeight,
      frontClassName,
      centerClassName,
      rearClassName,
    ]
  );

  return (
    <div className={classes.main} ref={ref} {...props}>
      {frontChildren && (
        <div className={classes.front} ref={refFront} {...frontProps}>
          {frontChildren}
        </div>
      )}
      <div className={classes.center} ref={refCenter} {...centerProps}>
        {children}
      </div>
      {rearChildren && (
        <div className={classes.rear} ref={refRear} {...rearProps}>
          {rearChildren}
        </div>
      )}
    </div>
  );
});
